<template>
  <v-container fluid class="align-start align-content-start fill-height">
    <EmployeeListHeader
      @search="patchDynamicSearchFiled($event)"
      @activeTab="selectedTab = $event"
      @import="showImportDialog = true"
      @exportCorporationAdminsList="exportCorporationAdminsList($event)"
      @exportBusinessBookingRecipientsList="exportBusinessBookingRecipientsList($event)"
      @addEmployeeClick="addEmployeeClick()"
      @addEmployeeGroupClick="addEmployeeGroupClick()"
    />

    <EmployeeList
      v-if="selectedTab && selectedTab.tab !== 'groups'"
      :employees="getEntities(selectedTab.tab)"
      :employeesLoading="getLoading(selectedTab.tab)"
      :selectedTab="selectedTab.tab"
      :tableHeader="getHeader(selectedTab.tab)"
      :totalPassengers="getTotalPassengers(selectedTab.tab)"
      @loadData="handleLoadByOptions"
      @resendEmailClick="resendEmailClick($event)"
      @editEmployeeClick="editEmployeeClick($event)"
      @deleteEmployeeClick="deleteEmployeeClick($event)"
    />

    <EmployeeListGroups
      v-else
      ref="employeeListGroups"
      :groupsLoading="businessGuestsLoading"
      :groups="businessPassengerGroups"
      :totalGroups="totalBusinessPassengerGroups"
      @loadData="handleLoadByOptions"
      @deleteEmployeeGroupClick="deleteEmployeeGroupClick($event)"
    />

    <dialog-wrapper
      ref="employeeDialog"
      :width="dialogWidth"
      @closeDialog="refreshData(selectedTab.tab)"
    />

    <v-dialog width="600" :value="showEmployeeForm">
      <create-employee-form
        :employeeData="employee"
        :mode="mode"
        @closeDialog="closeDialog($event, 'employee')"
      />
    </v-dialog>

    <v-dialog width="600" :value="showGuestForm">
      <create-guest-form
        :guestData="employee"
        :mode="mode"
        @closeDialog="closeDialog($event, 'guest')"
      />
    </v-dialog>

    <v-dialog width="600" :value="showCorpoAdminForm">
      <create-corpo-admin-form
        :corpoData="employee"
        :mode="mode"
        @closeDialog="closeDialog($event, 'corporationAdmin')"
      />
    </v-dialog>
    <v-dialog persistent width="680" :value="showImportDialog">
      <employee-import @cancel="showImportDialog = false" />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

import EmployeeListHeader from "@/components/employee/v2/EmployeeListHeader.vue";
import EmployeeList from "@/components/employee/v2/EmployeeList.vue";
import EmployeeListGroups from "@/components/employee/v2/EmployeeListGroups.vue";
import DialogWrapper from "@/components/elements/DialogWrapper.vue";

import EmployeeImport from "@/components/employee/EmployeeImport.vue";
import CreateEmployeeForm from "@/components/employee/v2/form/CreateEmployeeForm.vue";
import CreateGuestForm from "@/components/employee/v2/form/CreateGuestForm.vue";
import CreateCorpoAdminForm from "@/components/employee/v2/form/CreateCorpoAdminForm.vue";

import { businessPassenger, exportPassengers } from "@/mixins/passenger.mixins";

export default {
  components: {
    EmployeeListHeader,
    CreateEmployeeForm,
    CreateGuestForm,
    CreateCorpoAdminForm,
    EmployeeImport,
    DialogWrapper,
    EmployeeList,
    EmployeeListGroups,
  },
  mixins: [businessPassenger, exportPassengers],
  data() {
    return {
      employee: null,
      mode: "add",
      dialogWidth: 600,
      showImportDialog: false,
      selectedTab: null,
      showEmployeeForm: false,
      showGuestForm: false,
      showCorpoAdminForm: false,
    };
  },
  computed: {
    tableHeader() {
      return [
        {
          text: this.$t("components.employeeList.list.createdAt"),
          align: "left",
          sortable: true,
          value: "createdAt",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.name"),
          align: "left",
          sortable: false,
          value: "givenName",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.email"),
          align: "left",
          sortable: false,
          value: "email",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.group"),
          align: "left",
          sortable: false,
          value: "group",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.phone"),
          align: "left",
          sortable: false,
          value: "passenger.phoneNumber",
          class: "text-uppercase",
        },
        {
          value: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    guestTableHeader() {
      return [
        {
          text: this.$t("components.employeeList.list.createdAt"),
          align: "left",
          sortable: true,
          value: "createdAt",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.name"),
          align: "left",
          sortable: false,
          value: "givenName",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.phone"),
          align: "left",
          sortable: false,
          value: "phoneNumber",
          class: "text-uppercase",
        },
        {
          value: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    corporationAdminsHeader() {
      return [
        {
          text: this.$t("components.employeeList.list.createdAt"),
          align: "left",
          sortable: true,
          value: "createdAt",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.name"),
          align: "left",
          sortable: false,
          value: "givenName",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.employeeList.list.email"),
          align: "left",
          sortable: false,
          value: "email",
          class: "text-uppercase",
        },
        {
          text: this.$t("components.usersList.userList.list.role"),
          align: "left",
          sortable: false,
          value: "role",
          class: "text-uppercase",
        },
        {
          value: "actions",
          sortable: false,
          align: "end",
        },
      ];
    },
    ...mapGetters("accountsApiStore", ["accountSelected"]),
  },
  watch: {
    selectedTab(val) {
      if (val) {
        this.initCallPassengers(val.tab);
      }
    },
    // Since for the corporationAdmin tab, the data is not fetched by the initCallPassengers method,
    // we need to manually filter the tabs and call the getCorporationAdmins method to fetch the data.
    // This is only needed when the user changes the selected corporation, to refresh the list shown in the table.
    accountSelected(val) {
      if (val) {
        this.initCallPassengers(this.selectedTab.tab);
      }
    },
  },
  methods: {
    closeDialog(submitted, tab) {
      if (tab === "corporationAdmin") {
        this.showCorpoAdminForm = false;
      } else if (tab === "guest") {
        this.showGuestForm = false;
      } else {
        this.showEmployeeForm = false;
      }

      // Resets the employee data to make sure the form watcher is triggered when the dialog is opened again
      this.employee = null;
      if (submitted) {
        this.initCallPassengers(this.selectedTab.tab);
      }
    },
    patchDynamicSearchFiled(value) {
      if (this.selectedTab.tab === "employee") {
        this.searchObject.dynamicSearch = value;
        this.searchObject.page = 0;
      } else if (this.selectedTab.tab === "guest") {
        this.searchGuestObject.dynamicSearch = value;
        this.searchGuestObject.page = 0;
      } else if (this.selectedTab.tab === "groups") {
        this.searchPassengerGroupsObject.dynamicSearch = value;
        this.searchPassengerGroupsObject.page = 0;
      } else if (this.selectedTab.tab === "corporationAdmin") {
        this.searchCorporationAdminsObject.dynamicSearch = value;
        this.searchCorporationAdminsObject.page = 0;
      }
    },
    handleLoadByOptions(options) {
      const { page, sortBy, sortDesc, itemsPerPage } = options;

      if (this.selectedTab.tab === "employee") {
        const order = sortBy[0] || "createdAt";
        this.searchObject = {
          ...this.searchObject,
          page: page,
          maxPerPage: itemsPerPage,
          orderBy: sortDesc[0] ? `-${order}` : order,
        };
      } else if (this.selectedTab.tab === "guest") {
        const order = sortBy[0] || "createdAt";
        this.searchGuestObject = {
          ...this.searchGuestObject,
          page: page,
          maxPerPage: itemsPerPage,
          orderBy: sortDesc[0] ? `-${order}` : order,
        };
      } else if (this.selectedTab.tab === "groups") {
        const order = sortBy[0] || "createdAt";
        this.searchPassengerGroupsObject = {
          ...this.searchPassengerGroupsObject,
          page: page,
          maxPerPage: itemsPerPage,
          sort: sortDesc[0] ? `-${order}` : order,
        };
      } else if (this.selectedTab.tab === "corporationAdmin") {
        const order = sortBy[0] || "createdAt";
        this.searchCorporationAdminsObject = {
          ...this.entitySearchObject,
          page: page,
          maxPerPage: itemsPerPage,
          sort: sortDesc[0] ? `-${order}` : order,
        };
      }
    },
    async resendEmailClick(employee) {
      this.dialogWidth = 400;
      this.$refs.employeeDialog.openDialog({
        type: "resend-email",
        id: employee.id,
        corporationId: employee.corporationId || employee.businessAccountId,
      });
    },
    async deleteEmployeeClick(employee) {
      this.dialogWidth = 400;

      const endpoint = () => {
        switch (this.selectedTab.tab) {
          case "employee":
            return "delete-user";
          case "guest":
            return "delete-guest";
          case "corporationAdmin":
            return "delete-business-user";
          default:
            // Since groups cannot be deleted from this view, we should never reach this case
            return "";
        }
      };

      this.$refs.employeeDialog.openDialog({
        type: endpoint(),
        id: employee.id,
      });
    },
    deleteEmployeeGroupClick(group) {
      this.dialogWidth = 400;
      this.$refs.employeeDialog.openDialog({ type: "delete-group", id: group.id });
    },
    addEmployeeClick() {
      this.mode = "add";
      this.employee = null;

      switch (this.selectedTab.tab) {
        case "corporationAdmin":
          this.employee = {
            role: "corporationAdmin",
          };

          this.showCorpoAdminForm = true;
          break;
        case "guest":
          this.employee = {
            role: "guest",
          };

          this.showGuestForm = true;
          break;

        case "employee":
          this.employee = {
            role: "employee",
          };

          this.showEmployeeForm = true;
          break;
        default:
          break;
      }
    },
    addEmployeeGroupClick() {
      this.$refs.employeeListGroups.toggleGroupCreation();
    },
    async editEmployeeClick(employee) {
      switch (this.selectedTab.tab) {
        case "corporationAdmin":
          this.employee = this.corporationAdmins.find((corpoAdmin) => corpoAdmin.id === employee.id);
          this.employee.role = "corporationAdmin";

          this.mode = "edit";
          this.showCorpoAdminForm = true;
          break;
        case "guest":
          this.employee = this.guestPassengers.find((guest) => guest.id === employee.id);

          this.mode = "edit";
          this.showGuestForm = true;
          break;
        case "employee":
          this.employee = this.businessPassengers.find((businessPassenger) => businessPassenger.id === employee.id);

          this.mode = "edit";
          this.showEmployeeForm = true;
          break;
        default:
          this.employee =
          (
            await this.$store.dispatch("employee/findUsers", {
              ids: [employee.id],
              page: 0,
            })
          ).data[0] || null;
          break;
      }
    },
    getEntities(selectedTab) {
      switch (selectedTab) {
        case "employee":
          return this.businessPassengers;
        case "guest":
          return this.guestPassengers;
        case "corporationAdmin":
          return this.corporationAdmins;
        default:
          return [];
      }
    },
    getLoading(selectedTab) {
      switch (selectedTab) {
        case "employee":
          return this.businessPassengersLoading;
        case "guest":
          return this.guestPassengersLoading;
        case "corporationAdmin":
          return this.corporationAdminsLoading;
        default:
          return false;
      }
    },
    getHeader(selectedTab) {
      switch (selectedTab) {
        case "employee":
          return this.tableHeader;
        case "guest":
          return this.guestTableHeader;
        case "corporationAdmin":
          return this.corporationAdminsHeader;
        default:
          return [];
      }
    },
    getTotalPassengers(selectedTab) {
      switch (selectedTab) {
        case "employee":
          return this.totalBusinessPassengers;
        case "guest":
          return this.totalGuestPassengers;
        case "corporationAdmin":
          return this.totalCorporationAdmins;
        default:
          return 0;
      }
    },
    refreshData(tab) {
      this.initCallPassengers(tab);
    },
  },
};
</script>
